// TIP-487 (MV Stage 1b) — global "acting as company" identity.
// Membership is per company, so the app needs to know which of the user's
// companies they are currently acting as. Persisted to localStorage so the
// choice survives a reload.
//
// It used to say "cleared on logout (authSlice.clearUserInfo already removes
// this key)". That was half true and the half that was missing was a bug:
// clearUserInfo removes the localStorage KEY, but this slice's in-memory value
// survived, and `initialState` reads localStorage once at module load. Logging
// out and straight back in as somebody else is a client-side navigation with
// no reload, so user B arrived still acting as user A's company and every
// company-scoped request 403'd. See the extraReducers below.
import { createSlice } from "@reduxjs/toolkit";
import { clearIdentityScopedState, clearUserInfo, logout } from "./auth/authSlice";

export const STORAGE_KEY = "userSelectedCompany";

export const loadActiveCompany = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

const initialState = {
  activeCompany: loadActiveCompany(),
};

const activeCompanySlice = createSlice({
  name: "activeCompany",
  initialState,
  reducers: {
    setActiveCompany: (state, action) => {
      state.activeCompany = action.payload;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(action.payload));
      } catch (err) {
        // ignore write errors
      }
    },
    clearActiveCompany: (state) => {
      state.activeCompany = null;
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        // ignore
      }
    },
  },
  // Losing the session means losing the company it was acting as. Wired here
  // rather than at the six logout call sites, so a new one cannot forget it.
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) =>
        action.type === logout.type ||
        action.type === clearUserInfo.type ||
        action.type === clearIdentityScopedState.type,
      (state) => {
        state.activeCompany = null;
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (err) {
          // ignore
        }
      }
    );
  },
});

export const { setActiveCompany, clearActiveCompany } = activeCompanySlice.actions;

export const selectActiveCompany = (s) => s.activeCompany.activeCompany;
export const selectActiveCompanyId = (s) => s.activeCompany.activeCompany?.companyID ?? null;

export default activeCompanySlice.reducer;
